import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  FileText,
  ShoppingCart,
  CircleCheck,
  Sparkles,
  Clock,
  GitCompare,
} from "lucide-react";
import { Button } from "../ui/button";
import ProductVisual from "./ProductVisual";
import { useLocale } from "../../i18n/LocaleContext";

export default function ProductCard({ product, onAdd, onSubmitRfq, onOpenSpec, compareSelected, onToggleCompare, compareDisabled }) {
  const { t, fmt, fmtMoney } = useLocale();
  const [mfgVisible, setMfgVisible] = useState(false);
  const [qty, setQty] = useState(1);

  const hasMatch = typeof product.match_score === "number";
  const isHundred = hasMatch && product.match_score >= 100;
  const isRfqOnly = product.rfq_only || product.selling_price == null;

  return (
    <article
      data-testid={`product-card-${product.infoshop_pn}`}
      className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col hover:border-[#0072CE] hover:shadow-md transition-all"
    >
      {/* Image / visual */}
      <div className="relative aspect-[4/3] border-b border-slate-100">
        <ProductVisual product={product} className="absolute inset-0 w-full h-full" />
        {hasMatch && (
          <div
            className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] ${
              isHundred
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-slate-100 text-slate-700 border border-slate-200"
            }`}
            data-testid={`product-match-${product.infoshop_pn}`}
          >
            <CircleCheck size={12} />
            {product.match_score}% match
          </div>
        )}
        {isRfqOnly && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] bg-amber-50 text-amber-800 border border-amber-200">
            <Sparkles size={12} /> RFQ Only
          </div>
        )}
        {/* Compare toggle (top-right, lives below the RFQ-only badge when present) */}
        {onToggleCompare && !isRfqOnly && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (compareDisabled && !compareSelected) return;
              onToggleCompare(product);
            }}
            data-testid={`product-compare-toggle-${product.infoshop_pn}`}
            aria-pressed={!!compareSelected}
            disabled={compareDisabled && !compareSelected}
            title={
              compareSelected
                ? t("compare.remove_from_compare")
                : compareDisabled
                  ? t("compare.limit_reached")
                  : t("compare.add_to_compare")
            }
            className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] border transition-colors ${
              compareSelected
                ? "bg-[#0072CE] text-white border-[#0072CE]"
                : compareDisabled
                  ? "bg-white/80 text-slate-300 border-slate-200 cursor-not-allowed"
                  : "bg-white/95 text-slate-700 border-slate-300 hover:border-[#0072CE] hover:text-[#0072CE]"
            }`}
          >
            <GitCompare size={12} />
            {compareSelected ? t("compare.selected") : t("compare.compare")}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
          {product.category}
        </div>
        <h3 className="mt-1 font-display text-[15px] font-semibold text-slate-900 tracking-tight leading-tight line-clamp-2">
          {product.description}
        </h3>
        <div className="mt-2 text-sm text-slate-600">{product.brand}</div>

        {/* Identifiers */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-md bg-slate-50 border border-slate-100 px-2.5 py-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              {t("card.infoshop_pn")}
            </div>
            <div className="font-mono text-slate-900 text-[12px] font-semibold">
              {product.infoshop_pn}
            </div>
          </div>
          <div className="rounded-md bg-slate-50 border border-slate-100 px-2.5 py-1.5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                {t("card.mfg_pn")}
              </div>
              <button
                type="button"
                onClick={() => setMfgVisible((v) => !v)}
                data-testid={`mfg-eye-${product.infoshop_pn}`}
                className="text-slate-400 hover:text-[#0072CE] transition-colors"
                aria-label={mfgVisible ? "Hide manufacturer part number" : "Show manufacturer part number"}
              >
                {mfgVisible ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
            </div>
            <div className="font-mono text-slate-900 text-[12px] font-semibold tracking-wider">
              {mfgVisible ? product.mfg_pn : "•••••••"}
            </div>
          </div>
        </div>

        {/* Category + UNSPSC compact tags */}
        <div
          className="mt-2 flex flex-wrap items-center gap-1.5"
          data-testid={`product-taxonomy-${product.infoshop_pn}`}
        >
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-[#0072CE]/20 bg-[#0072CE]/5 text-[#0072CE]">
            <span className="uppercase tracking-[0.14em]">Category</span>
            <span className="font-normal text-slate-700 normal-case tracking-normal">{product.category}</span>
          </span>
          {product.unspsc && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-slate-200 bg-slate-50 text-slate-700"
              title={`UNSPSC ${product.unspsc} — United Nations Standard Products & Services Code`}
              data-testid={`unspsc-${product.infoshop_pn}`}
            >
              <span className="uppercase tracking-[0.14em] text-slate-500">UNSPSC</span>
              <span className="font-mono">{product.unspsc}</span>
            </span>
          )}
        </div>

        {/* Meta */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Clock size={12} /> {product.lead_time_days} {t("card.days")} {t("card.lead_time").toLowerCase()}
          </span>
          <span>{t("card.uom")}: {product.uom}</span>
          {product.spec_document_url && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onOpenSpec) {
                  onOpenSpec(product);
                } else {
                  // Fallback to opening in a new tab when no handler is wired
                  window.open(product.spec_document_url, "_blank", "noopener,noreferrer");
                }
              }}
              data-testid={`spec-link-${product.infoshop_pn}`}
              className="inline-flex items-center gap-1 text-[#0072CE] hover:underline"
            >
              <FileText size={12} /> {t("card.spec_sheet")}
            </button>
          )}
        </div>
        {product.dimensions && (
          <div className="mt-1.5 text-xs text-slate-500">Dimensions: {product.dimensions}</div>
        )}

        {/* Pricing block */}
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3.5">
          {isRfqOnly ? (
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-800">
                Price on request
              </div>
              <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                No catalog price configured. Submit an Instant RFQ — the Infosys Tactical Buying Desk will respond within 2 business days.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-slate-500 line-through">
                  {fmtMoney(product.list_price, { source_type: product.source_type, currency: product.currency })}
                </span>
                {product.effective_customer_discount_pct > 0 && (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                    −{product.effective_customer_discount_pct}%
                  </span>
                )}
              </div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900 tracking-tight">
                {fmtMoney(product.selling_price, { source_type: product.source_type, currency: product.currency })}
              </div>
              <div className="text-[11px] text-slate-500">{t("card.selling_price")} · {t("card.per")} {product.uom}</div>

              {/* Tax block — Avalara AvaTax-ready engine. When the buyer
                  has a matching exemption certificate on file, we replace
                  the standard tax line with an "Exempt · certificate on file"
                  badge. Otherwise we show inclusive vs exclusive per country. */}
              {product.tax && (
                <div
                  data-testid={`product-tax-${product.infoshop_pn}`}
                  className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]"
                >
                  {product.tax.is_exempt ? (
                    <>
                      <span
                        className="inline-flex items-center gap-1 text-emerald-700 font-semibold"
                        data-testid={`product-tax-exempt-${product.infoshop_pn}`}
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {t("card.tax_exempt")} · {t("card.tax_exempt_help")}
                      </span>
                      <span className="text-emerald-700 font-mono font-semibold">
                        0%
                      </span>
                    </>
                  ) : product.tax.display_mode === "inclusive" ? (
                    <>
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {t("card.tax_incl")} {product.tax.label} {product.tax.rate_pct}%
                      </span>
                      <span className="text-slate-500 font-mono">
                        +{fmtMoney(product.tax.unit_tax_amount, { source_type: product.source_type, currency: product.currency })}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 text-slate-600 font-semibold">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
                        {t("card.tax_excl")} {product.tax.label} {product.tax.rate_pct}%
                      </span>
                      <span className="text-slate-500 font-mono">
                        +{fmtMoney(product.tax.unit_tax_amount, { source_type: product.source_type, currency: product.currency })} {t("card.tax_at_checkout")}
                      </span>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-stretch gap-2">
          {isRfqOnly ? (
            <Button
              onClick={() => onSubmitRfq(product)}
              data-testid={`rfq-btn-${product.infoshop_pn}`}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-md font-semibold"
            >
              {t("card.rfq_btn")}
            </Button>
          ) : (
            <>
              <div className="flex items-center border border-slate-300 rounded-md overflow-hidden shrink-0">
                <button
                  type="button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  data-testid={`qty-dec-${product.infoshop_pn}`}
                  className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-50"
                  aria-label="Decrease quantity"
                >−</button>
                <input
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                  data-testid={`qty-input-${product.infoshop_pn}`}
                  className="w-10 text-center text-sm font-medium outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => setQty((q) => q + 1)}
                  data-testid={`qty-inc-${product.infoshop_pn}`}
                  className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-50"
                  aria-label="Increase quantity"
                >+</button>
              </div>
              <Button
                onClick={() => onAdd(product, qty)}
                data-testid={`add-cart-${product.infoshop_pn}`}
                className="flex-1 min-w-0 bg-[#0072CE] hover:bg-[#005BA6] text-white rounded-md font-semibold inline-flex items-center justify-center gap-1.5 text-sm px-2 whitespace-nowrap"
              >
                <ShoppingCart size={15} className="shrink-0" />
                <span className="truncate">{t("card.add_to_cart")}</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}
